import { jsonp } from './librivoxJsonp';

const BASE_URL = 'https://librivox.org/api/feed/audiobooks';

export interface LibriVoxBook {
  id: string;
  title: string;
  authors: { first_name: string; last_name: string }[];
  language: string;
  description: string;
  coverart_thumbnail?: string;
  url_iarchive?: string;
  sections?: LibriVoxSection[];
}

export interface LibriVoxSection {
  section_number: string;
  title: string;
  listen_url: string;
  playtime: string;
}

/**
 * Função para normalizar a URL da capa do LibriVox.
 * 1. Muda http para https na capa oficial.
 * 2. Se nula, extrai identificador da URL do Internet Archive para gerar capa.
 * 3. Se falhar ou não existir, retorna a URL da Open Library.
 */
export const getLibriVoxCoverUrl = (url: string | undefined, id: string, title: string, url_iarchive?: string): string => {
  // 1. Capa oficial do LibriVox (Thumbnail)
  if (url && url.trim() !== '' && !url.includes('librivox.org/api')) {
    return url.replace(/^http:\/\//i, 'https://');
  }

  // 2. Identificador do Internet Archive
  // Geralmente: https://archive.org/details/nome_do_livro
  if (url_iarchive && url_iarchive.includes('archive.org')) {
    try {
      const parts = url_iarchive.split('/').filter(p => p.trim() !== '');
      const identifier = parts[parts.length - 1];
      if (identifier && identifier !== 'details') {
        return `https://archive.org/services/img/${identifier}`;
      }
    } catch (e) {
      console.warn("[LibriVox] Falha ao extrair ID do Archive.org");
    }
  }

  // 3. Fallback: Open Library
  return `https://covers.openlibrary.org/b/title/${encodeURIComponent(title)}-L.jpg`;
};

export const librivoxApi = {
  searchAudiobooks: async ({ query = '', language = '', page = 0, limit = 10 }) => {
    let url = `${BASE_URL}?format=jsonp&limit=${limit}&offset=${page * limit}`;
    
    if (language && language.trim() !== '') {
      url += `&language=^${encodeURIComponent(language)}`;
    }

    if (query && query.trim() !== '') {
      url += `&title=^${encodeURIComponent(query)}`;
    }

    try {
      const response = await jsonp(url);
      const books = (response.books || []).map((book: any) => ({
        ...book,
        coverart_thumbnail: getLibriVoxCoverUrl(book.coverart_thumbnail, book.id, book.title, book.url_iarchive)
      }));
      return books;
    } catch (error) {
      console.error("[LibriVox API Error] Falha na requisição:", error);
      throw error;
    }
  },

  getAudiobookById: async (id: string): Promise<LibriVoxBook | null> => {
    const url = `${BASE_URL}?id=${id}&extended=1&format=jsonp`;
    
    const response = await jsonp(url);
    if (!response.books || response.books.length === 0) return null;
    
    const book = response.books[0];
    
    let sections: LibriVoxSection[] = [];
    if (book.sections) {
      sections = Object.values(book.sections).map((s: any) => ({
        section_number: s.section_number,
        title: s.title || `Capítulo ${s.section_number}`,
        listen_url: s.listen_url || '',
        playtime: s.playtime || '0:00'
      }));
    }

    return {
      ...book,
      coverart_thumbnail: getLibriVoxCoverUrl(book.coverart_thumbnail, book.id, book.title, book.url_iarchive),
      sections
    };
  }
};